import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { callIntelKpiUtils } from '@frontend/call-intel-core';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Heading, SkeletonLoaders, Text, useTooltip } from '@frontend/design-system';

type KpiSummaryProps = {
  items: CallIntelTypes.KpiItem[];
  isLoading?: boolean;
};

export const KpiSummary: React.FC<KpiSummaryProps> = ({ items, isLoading, ...rest }) => {
  const { Tooltip: Tooltip, triggerProps, tooltipProps } = useTooltip({ placement: 'top' });

  if (isLoading) {
    return <SkeletonLoaders.Loader height='120px' {...rest} />;
  }

  return (
    <div css={styles.wrapper} {...rest}>
      {items.map((item) => {
        const formattedValue = callIntelKpiUtils.formatValue(item.value ?? 0, item.format ?? 'integer');
        const trend = item.trend;
        const showTrend = !!item.trend && !item.noCallsToAnalyze;
        const trendColor = callIntelKpiUtils.getTrendColor(
          item.trend?.change ?? 'unchanged',
          item.trend?.polarity ?? 'neutral'
        );
        const showTrendIndicator = trend?.change === 'increased' || trend?.change === 'decreased';

        return (
          <div key={item.id} css={styles.card}>
            <Text color='subdued' weight='bold'>
              {item.title}
            </Text>
            <div css={styles.valueRow}>
              <Heading level={1}>{formattedValue}</Heading>
              {showTrend && trend && (
                <div css={styles.trend(trendColor)} {...triggerProps}>
                  {showTrendIndicator && <Icon name={trend.change === 'increased' ? 'up-small' : 'down-small'} />}
                  <Text size='medium'>{item.trend?.percentageChange}%</Text>
                  {trend.tooltip && (
                    <Tooltip {...tooltipProps}>
                      <Text size='medium' css={styles.tooltipText}>
                        {trend.tooltip}
                      </Text>
                      <Text size='medium' css={styles.tooltipText}>
                        {trend.partialNote}
                      </Text>
                    </Tooltip>
                  )}
                </div>
              )}
            </div>
            <Text color='subdued' size='small'>
              {item.subtext}
            </Text>
          </div>
        );
      })}
    </div>
  );
};

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: row;
    gap: 124px;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    border: 1px solid ${theme.colors.neutral20};
    width: 100%;
    padding: ${theme.spacing(3)};
    border-radius: ${theme.borderRadius.medium};

    @container (max-width: ${breakpoints.xsmall.max}px) {
      justify-content: space-between;
      gap: ${theme.spacing(2)};
    }
  `,
  card: css`
    display: flex;
    flex-direction: column;
    align-items: center;
  `,

  valueRow: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,

  trend: (color: string) => css`
    color: ${color};
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.025)};
    flex-direction: row;

    svg {
      width: ${theme.spacing(1.5)};
      height: ${theme.spacing(1.5)};
    }

    p {
      color: ${color};
    }
  `,

  tooltipText: css`
    font-style: italic;
    color: ${theme.colors.white};
  `,
};
